import { useState, lazy, Suspense, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAtomValue } from 'jotai';
import { alpha } from '@mui/material/styles';
import { brandGradient } from '@/lib/themeHelpers';
import { isAuthenticatedAtom } from '@/store/authAtoms';
import { useConfig, isYes, isEnabledByDefault, useAriaName } from '@/hooks/useConfig';
import { useLiteMode } from '@/hooks/useLiteMode';
import { useContent } from '@/hooks/useContent';
import { interpolateContent } from '@/lib/content';
import { launchLiveChat, getLiveChatDiagnostics, parseEmbedScripts } from '@/lib/scriptEmbedder';
import { useToast } from '@/components/common/Toast';
import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import Drawer from '@mui/material/Drawer';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import Paper from '@mui/material/Paper';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Popper from '@mui/material/Popper';
import useMediaQuery from '@mui/material/useMediaQuery';
import ChatIcon from '@mui/icons-material/Chat';
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import Face3Icon from '@mui/icons-material/Face3';
import CloseIcon from '@mui/icons-material/Close';

const DashboardAI = lazy(() => import('@/pages/DashboardAI'));

// Paths where the FAB should be suppressed (chat already on screen, or pre-auth flows).
const HIDDEN_ROUTES = new Set(['/dashboard-ai', '/login', '/sign-in', '/register', '/account-recovery', '/home', '/facility-selection']);

const FAB_SIZE = 60;
const DRAG_THRESHOLD = 6;

/** Above SchedulingWizard (modal+4); below wizard confirm/order dialogs (modal+8/+10). */
const FAB_Z_INDEX = (theme: { zIndex: { modal: number } }) => theme.zIndex.modal + 5;

// Stored as right/bottom distances from viewport edges — stable across window resizes.
interface FabPos { right: number; bottom: number }
interface DragState { startX: number; startY: number; startRight: number; startBottom: number; moved: boolean }

function readPersistedPos(): FabPos | null {
  try {
    const raw = localStorage.getItem('ariaFabPos');
    if (raw) return JSON.parse(raw) as FabPos;
  } catch { /* ignore */ }
  return null;
}

export function AriaFab() {
  const { getContent, getContentString } = useContent();
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthed = useAtomValue(isAuthenticatedAtom);
  const ariaName = useAriaName();
  const dashboardAiEnabled = isYes(useConfig('PPDashboardAIEnabled'));
  const messagingEnabled = isYes(useConfig('PPMessagesAccessEnabled'));
  const helpEnabled = isEnabledByDefault(useConfig('PPGetHelpAccessEnabled'));
  const liveChatEnabled = isYes(useConfig('PPLiveChatEnabled'));
  const lite = useLiteMode();
  const showAria = dashboardAiEnabled && lite.isSectionVisible('aria');
  const headerEmbedScripts = useConfig('PPHeaderEmbedScripts') ?? '';
  const showLiveChat = liveChatEnabled && parseEmbedScripts(headerEmbedScripts).length > 0;
  const isMobile = useMediaQuery('(max-width: 1023px)');

  const [menuOpen, setMenuOpen] = useState(false);
  const [ariaOpen, setAriaOpen] = useState(false);
  // null → CSS default positioning. non-null → explicit right/bottom px from viewport edges.
  const [pos, setPos] = useState<FabPos | null>(readPersistedPos);
  const [isDragging, setIsDragging] = useState(false);
  // Drag bookkeeping in a ref to avoid stale-closure issues — never triggers re-renders mid-drag.
  const drag = useRef<DragState | null>(null);
  const fabRef = useRef<HTMLButtonElement | null>(null);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    drag.current = {
      startX: e.clientX,
      startY: e.clientY,
      startRight: window.innerWidth - rect.right,
      startBottom: window.innerHeight - rect.bottom,
      moved: false,
    };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    e.preventDefault();
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved && (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD)) {
      d.moved = true;
      setIsDragging(true);
      setMenuOpen(false);
    }
    if (!d.moved) return;
    const w = window.innerWidth;
    const h = window.innerHeight;
    setPos({
      right:  Math.max(8, Math.min(w - FAB_SIZE - 8, d.startRight  - dx)),
      bottom: Math.max(8, Math.min(h - FAB_SIZE - 8, d.startBottom - dy)),
    });
  }, []);

  const handlePointerUp = useCallback(() => {
    const d = drag.current;
    if (!d) return;
    const { moved } = d;
    drag.current = null;
    setIsDragging(false);
    if (moved) {
      setPos(prev => {
        if (prev) {
          try { localStorage.setItem('ariaFabPos', JSON.stringify(prev)); } catch { /* ignore */ }
        }
        return prev;
      });
    } else {
      setMenuOpen((open) => !open);
    }
  }, []);

  const showSendMessage =
    (messagingEnabled && lite.isSectionVisible('messages'))
    || (helpEnabled && lite.isSectionVisible('help'));
  const showAnyOption = showSendMessage || showAria || showLiveChat;

  // ── All hooks above; early returns below ─────────────────────────────────────
  if (!isAuthed || !showAnyOption) return null;
  if (HIDDEN_ROUTES.has(location.pathname)) return null;

  const drawerWidth = isMobile ? '100vw' : 420;
  // Shift up on mobile so we clear the bottom nav (≈56px + safe area).
  // Shift up on desktop so we clear the footer banner (≈44px) with breathing room.
  const fabBottom = isMobile ? 'calc(72px + env(safe-area-inset-bottom, 0px))' : 80;
  const ariaLabel = interpolateContent(getContentString('PPNavDashboardAI'), { name: ariaName });
  const fabMenuLabel = getContentString('PPFabMenuAriaLabel') || 'Contact and messaging options';

  const handleSendMessage = () => {
    setMenuOpen(false);
    if (messagingEnabled && lite.isSectionVisible('messages')) {
      navigate('/messages?compose=1');
      return;
    }
    navigate('/help');
  };

  const handleLiveChat = () => {
    setMenuOpen(false);
    void launchLiveChat().then((ok) => {
      if (!ok) {
        console.warn('[LiveChat] Salesforce Embedded Messaging is not ready', getLiveChatDiagnostics());
        toast(
          getContentString('PPFabLiveChatUnavailable')
            || 'Live chat is not available right now. Please try again in a moment.',
          'info',
        );
      }
    });
  };

  const handleAskAria = () => {
    setMenuOpen(false);
    setAriaOpen(true);
  };

  return (
    <>
      {!ariaOpen && (
        <ClickAwayListener onClickAway={() => setMenuOpen(false)}>
          <Box
            sx={{
              position: 'fixed',
              ...(pos
                ? { bottom: pos.bottom, right: pos.right }
                : { bottom: fabBottom, insetInlineEnd: 24 }),
              zIndex: FAB_Z_INDEX,
            }}
          >
            <Tooltip title={isDragging || menuOpen ? '' : fabMenuLabel} placement="left">
              <Fab
                ref={fabRef}
                aria-label={fabMenuLabel}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
                sx={(theme) => ({
                  width: FAB_SIZE,
                  height: FAB_SIZE,
                  color: 'common.white',
                  bgcolor: 'primary.main',
                  backgroundImage: brandGradient(theme),
                  opacity: 1,
                  boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.35)}`,
                  cursor: isDragging ? 'grabbing' : 'grab',
                  userSelect: 'none',
                  touchAction: 'none',
                  animation: 'none',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                    backgroundImage: brandGradient(theme),
                    transform: isDragging ? 'none' : 'scale(1.06)',
                    opacity: 1,
                  },
                  transition: isDragging ? 'none' : 'transform 150ms ease',
                })}
              >
                {menuOpen ? <CloseIcon sx={{ fontSize: 28 }} /> : <ChatIcon sx={{ fontSize: 28 }} />}
              </Fab>
            </Tooltip>

            <Popper
              open={menuOpen}
              anchorEl={fabRef.current}
              placement="top-end"
              modifiers={[{ name: 'offset', options: { offset: [0, 12] } }]}
              sx={{ zIndex: FAB_Z_INDEX }}
            >
              <Paper
                elevation={4}
                role="menu"
                aria-label={fabMenuLabel}
                sx={{
                  borderRadius: 1,
                  minWidth: 220,
                  overflow: 'hidden',
                }}
              >
                <MenuList dense disablePadding sx={{ py: 0.5 }}>
                  {showSendMessage && (
                    <MenuItem
                      onClick={handleSendMessage}
                      sx={{ py: 1.25, px: 2, gap: 1.5 }}
                    >
                      <ListItemIcon sx={{ minWidth: 0, color: 'text.secondary' }}>
                        <ForwardToInboxOutlinedIcon fontSize="small" aria-hidden="true" />
                      </ListItemIcon>
                      <ListItemText
                        primary={getContent('PPFabSendMessage') || 'Send us a message'}
                        slotProps={{ primary: { variant: 'body2', sx: { fontWeight: 500 } } }}
                      />
                    </MenuItem>
                  )}

                  {showLiveChat && (
                    <MenuItem
                      onClick={handleLiveChat}
                      sx={{ py: 1.25, px: 2, gap: 1.5 }}
                    >
                      <ListItemIcon sx={{ minWidth: 0, color: 'text.secondary' }}>
                        <ForumOutlinedIcon fontSize="small" aria-hidden="true" />
                      </ListItemIcon>
                      <ListItemText
                        primary={getContent('PPFabLiveChat') || 'Live Chat'}
                        slotProps={{ primary: { variant: 'body2', sx: { fontWeight: 500 } } }}
                      />
                    </MenuItem>
                  )}

                  {showAria && (
                    <MenuItem
                      onClick={handleAskAria}
                      sx={{
                        py: 1.25,
                        px: 2,
                        gap: 1.5,
                        '&:hover .aria-menu-icon': {
                          color: 'primary.main',
                          transform: 'scale(1.08)',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 0, color: 'text.secondary' }}>
                        <Face3Icon
                          className="aria-menu-icon"
                          fontSize="small"
                          aria-hidden="true"
                          sx={{ transition: 'transform 150ms ease, color 150ms ease' }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={ariaLabel}
                        slotProps={{ primary: { variant: 'body2', sx: { fontWeight: 500 } } }}
                      />
                    </MenuItem>
                  )}
                </MenuList>
              </Paper>
            </Popper>
          </Box>
        </ClickAwayListener>
      )}

      <Drawer
        anchor="right"
        open={ariaOpen}
        onClose={() => setAriaOpen(false)}
        ModalProps={{ keepMounted: false }}
        sx={{
          zIndex: FAB_Z_INDEX,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            maxWidth: '100vw',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Drawer header */}
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.25,
            background: brandGradient(theme),
            color: 'common.white',
            flexShrink: 0,
          })}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: alpha('#fff', 0.2),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Face3Icon sx={{ fontSize: 20 }} />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
              {ariaLabel}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setAriaOpen(false)}
            aria-label={getContentString('PortalClose') || 'Close'}
            size="small"
            sx={{ color: 'common.white', minWidth: 24, minHeight: 24, '&:hover': { bgcolor: alpha('#fff', 0.15) } }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Chat body — fills remaining drawer height */}
        <Box sx={{ flex: 1, minHeight: 0, p: 1.5, overflow: 'hidden' }}>
          <Suspense fallback={<Skeleton variant="rounded" height="100%" sx={{ borderRadius: 3 }} />}>
            <DashboardAI embedded onNavigate={() => setAriaOpen(false)} />
          </Suspense>
        </Box>
      </Drawer>
    </>
  );
}
